import { Auth } from 'aws-amplify';

import { isPostHogLoaded, posthog } from '@lib/posthog.lib';
import { AuthUserSession } from '@lib/types/authUser.types';

type TokenRefreshStage =
  | 'noCurrentSession'
  | 'noAccessToken'
  | 'noRefreshToken'
  | 'refreshFailed'
  | 'caughtError';

const captureTokenRefreshIssue = (stage: TokenRefreshStage): void => {
  if (!isPostHogLoaded()) {
    return;
  }

  posthog.capture('tokenRefresh_issue', { stage });
};

/**
 * Refreshes the Cognito session when the access token is within five minutes
 * of expiry. Failures emit sanitized PostHog events without tokens or user data.
 * Missing Cognito user is expected for anonymous/login visits and is not emitted.
 */
export const refreshTokenIfNeeded = async (
  _authUserSession?: AuthUserSession
): Promise<void> => {
  let cognitoUser;
  let currentSession;
  let accessToken;
  let refreshToken;

  try {
    cognitoUser = await Auth.currentAuthenticatedUser();
  } catch {
    // Anonymous / logged-out visitors are expected; skip noisy false positives.
    return;
  }

  try {
    currentSession = await Auth.currentSession();
  } catch {
    captureTokenRefreshIssue('noCurrentSession');

    return;
  }

  try {
    accessToken = currentSession.getAccessToken();
  } catch {
    captureTokenRefreshIssue('noAccessToken');

    return;
  }

  try {
    refreshToken = currentSession.getRefreshToken();
  } catch {
    captureTokenRefreshIssue('noRefreshToken');

    return;
  }

  try {
    const expiration = accessToken.getExpiration();
    const currentTime = Math.floor(Date.now() / 1000);
    const tokenExpiryThreshold = 5 * 60;

    if (expiration - currentTime < tokenExpiryThreshold) {
      await cognitoUser.refreshSession(refreshToken, (err, session) => {
        if (err || !session) {
          captureTokenRefreshIssue('refreshFailed');
        }
      });
    }
  } catch {
    captureTokenRefreshIssue('caughtError');
  }
};
